import { Separator } from "@/components/ui/separator";
import { Minus, Plus } from "lucide-react";
import { applySmartPricing } from "@/utils/smartPricingUtils";
import { useTranslation } from "react-i18next";
import KaisoPrimeCheckoutBadge from "@/components/prime/KaisoPrimeCheckoutBadge";

/**
 * Sticky cart summary sidebar shown in step 2 of checkout.
 * Displays items, subtotal, smart pricing discount line, total.
 */
export default function CheckoutOrderSummary({ tc, fmt, cart, cartTotal, smartPricing, categories, isTableOrder, table, delivery, orderType, restaurant, payment, onUpdateQuantity, loyaltyReward, couponDiscount, primeDeliveryFee }) {
  const { t } = useTranslation();
  const categoryMap = {};
  for (const cat of (categories || [])) {
    if (cat.id) categoryMap[cat.id] = cat.name || "";
  }
  const marketingCouponApplied = couponDiscount?.valid === true && couponDiscount?.kind === "coupon";
  const referralPreviewApplied = couponDiscount?.valid === true
    && couponDiscount?.kind === "referral"
    && !smartPricing?.active;
  const { subtotal, discountAmount } = applySmartPricing(
    cart,
    marketingCouponApplied ? { active: false, discount: 0 } : smartPricing,
    categoryMap,
  );
  const loyaltyDiscount = marketingCouponApplied ? 0 : (loyaltyReward?.discount_amount || 0);
  const couponAmount = (marketingCouponApplied || referralPreviewApplied)
    ? Number(couponDiscount.discount_amount || 0)
    : 0;
  const finalTotal = marketingCouponApplied && couponDiscount.final_total != null
    ? Math.max(0, Number(couponDiscount.final_total))
    : Math.max(0, cartTotal - couponAmount - loyaltyDiscount);
  const zones = (restaurant?.delivery_zones || []).filter(z => z.active !== false).sort((a, b) => Number(a.max_km) - Number(b.max_km));
  const hasCoords = [restaurant?.lat, restaurant?.lng, delivery?.lat, delivery?.lng].every(v => v != null && Number.isFinite(Number(v)));
  let deliveryZone = null;
  if (!isTableOrder && orderType === "delivery" && hasCoords && zones.length) {
    const rad = n => n * Math.PI / 180;
    const dLat = rad(Number(delivery.lat) - Number(restaurant.lat));
    const dLng = rad(Number(delivery.lng) - Number(restaurant.lng));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(Number(restaurant.lat))) * Math.cos(rad(Number(delivery.lat))) * Math.sin(dLng / 2) ** 2;
    const km = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (!(Number(restaurant.delivery_free_km) > 0 && km <= Number(restaurant.delivery_free_km))) {
      deliveryZone = zones.find(z => km <= Number(z.max_km)) || zones[zones.length - 1];
    }
  }
  const freeAbove = deliveryZone?.free_above == null ? null : Number(deliveryZone.free_above);
  const deliveryFee = !isTableOrder && orderType === "delivery"
    ? (deliveryZone
      ? (freeAbove != null && cartTotal >= freeAbove ? 0 : Number(deliveryZone.fee || 0))
      : (hasCoords && zones.length ? 0 : Number(restaurant?.default_shipping_fee || 0)))
    : 0;
  const remaining = deliveryFee > 0 && freeAbove != null ? Math.max(0, freeAbove - cartTotal) : 0;
  const payableTotal = finalTotal + deliveryFee;
  const [pr, pg, pb] = tc.primary.replace("#","").match(/.{2}/g).map(h => parseInt(h,16));

  const paymentLabel = {
    online: "Online",
    cash: t("checkout.cashLabel"),
    bizum: "Bizum",
    mbway: "MBWay",
  }[payment.method] || t("checkout.cardAtRestaurant");

  return (
    <div className="bg-gray-50 rounded-xl p-4 border sticky top-4">
      <h3 className="font-bold text-sm mb-3" style={{ color: tc.primary }}>{t("checkout.summary")}</h3>

      {isTableOrder && table.name && (
        <div className="mb-3 px-2 py-1.5 rounded-lg text-xs font-medium"
          style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.06)`, color: tc.primary }}>
          {table.name || `Mesa ${table.number}`}
        </div>
      )}

      {!isTableOrder && delivery.full && (
        <p className="text-xs text-gray-500 mb-3">{delivery.full}</p>
      )}

      <div className="space-y-1.5 text-sm">
        {cart.map(item => (
          <div key={item.cartId || item.id} className="flex items-center justify-between gap-2">
            <span className="text-gray-600 flex-1 min-w-0 truncate">{item.name}</span>
            <div className="flex items-center gap-1 shrink-0">
              {onUpdateQuantity && (
                <button onClick={() => onUpdateQuantity(item.cartId || item.id, -1)}
                  className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition">
                  <Minus className="w-3 h-3 text-gray-500" />
                </button>
              )}
              <span className="text-gray-400 text-xs w-5 text-center">{item.quantity}x</span>
              {onUpdateQuantity && (
                <button onClick={() => onUpdateQuantity(item.cartId || item.id, 1)}
                  className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition">
                  <Plus className="w-3 h-3" style={{ color: tc.primary }} />
                </button>
              )}
            </div>
            <span className="font-medium shrink-0">{fmt(item.price * item.quantity)}</span>
          </div>
        ))}
      </div>

      {primeDeliveryFee > 0 && (
        <div className="mt-2 mb-1">
          <KaisoPrimeCheckoutBadge savedAmount={primeDeliveryFee} compact />
        </div>
      )}

      {orderType === "delivery" && !isTableOrder && (
        <div className="mt-3 text-sm" data-testid="delivery-fee-summary">
          <div className="flex justify-between"><span>Entrega</span><span>{deliveryFee > 0 ? fmt(deliveryFee) : "Gratis"}</span></div>
          {remaining > 0 && <p className="mt-1 text-xs text-emerald-700" data-testid="free-delivery-progress">Añade {fmt(remaining)} más al carrito y consigue el envío gratis.</p>}
        </div>
      )}
      <Separator className="my-3" />
      {(discountAmount > 0 || couponAmount > 0 || loyaltyDiscount > 0) ? (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-gray-500">
            <span>{t("checkout.subtotal")}</span>
            <span>{fmt(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-emerald-600 font-medium">
              <span>{t("checkout.discountPct", { pct: smartPricing.discount })}</span>
              <span>-{fmt(discountAmount)}</span>
            </div>
          )}
          {couponAmount > 0 && (
            <div className="flex justify-between text-emerald-600 font-medium" data-testid="coupon-discount-line">
              <span>Cupom {couponDiscount.code}</span>
              <span>-{fmt(couponAmount)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div className="flex justify-between text-amber-600 font-medium" data-testid="loyalty-discount-line">
              <span>🎁 {loyaltyReward.reward}</span>
              <span>-{fmt(loyaltyDiscount)}</span>
            </div>
          )}
          <Separator className="my-1" />
          <div className="flex justify-between font-bold text-base">
            <span>{t("checkout.total")}</span>
            <span style={{ color: tc.primary }}>{fmt(payableTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="flex justify-between font-bold text-base">
          <span>{t("checkout.total")}</span>
          <span style={{ color: tc.primary }}>{fmt(payableTotal)}</span>
        </div>
      )}

      {payment.method === "cash" && payment.changeFor && Number(payment.changeFor) > payableTotal && (
        <div className="mt-2 bg-amber-50 rounded-lg p-2 text-center">
          <span className="text-xs text-amber-600">{t("checkout.change")}: </span>
          <span className="font-bold text-amber-700">{fmt(Number(payment.changeFor) - payableTotal)}</span>
          <span className="text-xs text-amber-500 block">{t("checkout.paidWith", { amount: fmt(Number(payment.changeFor)) })}</span>
        </div>
      )}

      {payment.method && !(isTableOrder && table.paymentAtTableEnabled && !table.paymentEnabled) && (
        <div className="mt-2 text-xs text-gray-500 text-center">
          {t("checkout.paymentLine")}: {paymentLabel}
        </div>
      )}

      {isTableOrder && table.paymentAtTableEnabled && !table.paymentEnabled && (
        <div className="mt-2 text-xs text-emerald-600 text-center font-medium">
          {t("checkout.payAtEndRestaurant")}
        </div>
      )}
    </div>
  );
}
